import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Filter, ScrollText } from "lucide-react";
import { api } from "../../utils/api";

const ACTION_LABELS = {
  impersonate_start: "Started impersonation",
  impersonate_stop: "Stopped impersonation",
  waive_billing: "Waived billing",
  unwaive_billing: "Un-waived billing",
  delete_user: "Deleted user",
  deactivate_user: "Deactivated user",
  reactivate_user: "Reactivated user",
  impersonated_destructive: "Destructive action while impersonating",
  change_password: "Changed admin password",
};

const ACTION_TONES = {
  impersonate_start: "bg-blue-50 text-blue-700 ring-blue-600/20",
  impersonate_stop: "bg-gray-50 text-gray-700 ring-gray-600/20",
  waive_billing: "bg-green-50 text-green-700 ring-green-600/20",
  unwaive_billing: "bg-amber-50 text-amber-700 ring-amber-600/20",
  delete_user: "bg-red-50 text-red-700 ring-red-600/20",
  deactivate_user: "bg-red-50 text-red-700 ring-red-600/20",
  reactivate_user: "bg-green-50 text-green-700 ring-green-600/20",
  impersonated_destructive: "bg-red-50 text-red-700 ring-red-600/20",
  change_password: "bg-indigo-50 text-indigo-700 ring-indigo-600/20",
};

function fmtDateTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return "—";
  }
}

export default function AdminAuditLog() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("");

  useEffect(() => {
    setLoading(true);
    api
      .get("/admin/audit-log")
      .then((data) => setEntries(data.entries || []))
      .catch((err) => setError(err.message || "Failed to load audit log"))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    if (!filter) return entries;
    return entries.filter((e) => e.action === filter);
  }, [entries, filter]);

  const actionCounts = useMemo(() => {
    const c = {};
    entries.forEach((e) => {
      c[e.action] = (c[e.action] || 0) + 1;
    });
    return c;
  }, [entries]);

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
            Audit log
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {entries.length} total entries · newest first
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-gray-500" />
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="input-base h-9 text-sm"
          >
            <option value="">All actions</option>
            {Object.keys(ACTION_LABELS).map((a) => (
              <option key={a} value={a}>
                {ACTION_LABELS[a]} ({actionCounts[a] || 0})
              </option>
            ))}
          </select>
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex min-h-[50vh] items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="card flex flex-col items-center gap-2 p-10 text-center text-sm text-gray-500">
          <ScrollText className="h-6 w-6 text-gray-300" />
          No audit entries yet.
        </div>
      ) : (
        <div className="card p-0">
          <ul className="divide-y divide-gray-100 dark:divide-gray-800">
            {filtered.map((e) => {
              const tone = ACTION_TONES[e.action] || "bg-gray-50 text-gray-700 ring-gray-600/20";
              return (
                <li key={e.id} className="p-4">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="mb-1 flex flex-wrap items-center gap-2">
                        <span
                          className={`rounded-full px-2 py-0.5 text-[11px] font-medium ring-1 ring-inset ${tone}`}
                        >
                          {ACTION_LABELS[e.action] || e.action}
                        </span>
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          by {e.adminEmail}
                        </span>
                      </div>
                      {e.targetUid && (
                        <div className="text-sm text-gray-700 dark:text-gray-300">
                          Target:{" "}
                          <Link
                            to={`/admin/users/${e.targetUid}`}
                            className="font-medium text-indigo-600 hover:underline"
                          >
                            {e.targetEmail || e.targetUid}
                          </Link>
                        </div>
                      )}
                      {e.details && Object.keys(e.details).length > 0 && (
                        <pre className="mt-1 overflow-x-auto rounded bg-gray-50 px-2 py-1 text-[11px] text-gray-600 dark:bg-gray-900 dark:text-gray-400">
                          {JSON.stringify(e.details, null, 2)}
                        </pre>
                      )}
                    </div>
                    <div className="whitespace-nowrap text-xs text-gray-500 dark:text-gray-400">
                      {fmtDateTime(e.timestamp)}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
